'use client';

import { useActionState, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { RATING_REVIEWS_FORM_ID } from '@headless-workspace/config';
import {
    FormUtils,
    generateDefaultNickname,
    RatingAndReviewsFormValue,
    UserConnectionStatus,
    UserInfo,
} from '@headless-workspace/domain/common/server';
import {
    GlowButton,
    GlowDivider,
    GlowDropDown,
    GlowFieldWrapper,
    GlowInput,
    GlowPills,
    GlowStarRating,
    GlowTextArea,
    GlowTextBody,
    Icons,
    Pill,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component, NoEmptyArray } from '@headless-workspace/typings';

type RatingsAndReviewsFormState = {
    data: { [k: keyof RatingAndReviewsFormValue['fields']]: FormDataEntryValue | null };
    errors: undefined | Record<string, string[]>;
    success: boolean;
};

type RatingsAndReviewsFormProps = PropsWithLocale & {
    defaultEmail?: string;
    formInfo: RatingAndReviewsFormValue['fields'];
    userInfo: UserInfo;
    onSubmit: (isSuccess: boolean) => void;
};

const REVIEW_TITLE_MAX_LENGTH = 50;
const REVIEW_NICKNAME_MAX_LENGTH = 50;
const REVIEW_DETAILS_MAX_LENGTH = 1000;
const defaultPillValue = 'empty';

export const RatingsAndReviewsForm: Component<RatingsAndReviewsFormProps> = ({ formInfo, userInfo, onSubmit }) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');

    const pills: NoEmptyArray<Pill> = [
        { id: 'true', label: t('label.like'), icon: Icons.Like },
        { id: 'false', label: t('label.dislike'), icon: Icons.Dislike },
    ];

    const handleChangeNickname =
        (wrapperChange: (valueLength: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
            wrapperChange(e.target.value.length);
        };

    const initialValue: RatingsAndReviewsFormState = useMemo(
        () => ({
            data: {
                age: null,
                content: null,
                nickname: userInfo.status === UserConnectionStatus.Registered ? generateDefaultNickname(userInfo) : '',
                rating: null,
                recommended: null,
                title: null,
                usageDuration: null,
            },
            errors: undefined,
            success: false,
        }),
        [userInfo],
    );

    const [formSchema, setFormSchema] = useState<any>(null);
    const [starRating, setStarRating] = useState(0);
    const [isOpenAgeDropDown, setIsOpenAgeDropDown] = useState(false);
    const [recommended, setRecommended] = useState<string>();
    const [age, setAge] = useState<string>();
    const [usageDuration, setUsageDuration] = useState<string>();

    const submitForm = (prevState: any, formData: FormData) => {
        // TODO: Don't edit fields with empty string. Unfilled fields should be undefined.
        formData.append(formInfo['age'].id, age ?? '');
        formData.append(formInfo['usageDuration'].id, usageDuration ?? '');
        formData.append(formInfo['recommended'].id, recommended ?? '');
        formData.append(formInfo['rating'].id, starRating.toString());

        // TODO: Change how data is created. Ideally, we should use formData directly.
        const data = Object.fromEntries(
            [
                formInfo['age'].id,
                formInfo['content'].id,
                formInfo['nickname'].id,
                formInfo['rating'].id,
                formInfo['recommended'].id,
                formInfo['title'].id,
                formInfo['usageDuration'].id,
            ].map((key) => [key, formData.get(key)]),
        );

        const result = formSchema.safeParse(data);

        return result.success
            ? { data, errors: undefined, success: true }
            : { data, errors: result.error.formErrors.fieldErrors, success: false };
    };

    const [formState, formAction] = useActionState(submitForm, initialValue);

    useEffect(() => {
        setFormSchema(
            FormUtils.generateFormSchema(
                [
                    formInfo.age,
                    formInfo.usageDuration,
                    formInfo.content,
                    formInfo.nickname,
                    formInfo.title,
                    formInfo.rating,
                    formInfo.recommended,
                ],
                {
                    // TODO: For now, required string fields is translated to minLength: 1. To change.
                    [formInfo.title.id]: { minLength: 1, maxLength: 50 },
                    [formInfo.nickname.id]: { minLength: 1 },
                    [formInfo.content.id]: {
                        maxLength: 1000,
                    },
                },
            ),
        );
    }, [formInfo]);

    useEffect(() => {
        if (formState?.success) {
            onSubmit(formState.success);
        }
    }, [formState, onSubmit]);

    const error = formState?.errors;

    return (
        <form action={formAction} className={'flex flex-1 flex-col'} id={RATING_REVIEWS_FORM_ID} noValidate>
            <div className={'flex flex-1 flex-col gap-1.5 pb-1'}>
                <GlowDivider />

                <GlowFieldWrapper
                    className={'block'}
                    fieldId={formInfo.rating.id}
                    required={formInfo.rating.isRequired}
                    errorText={error?.rating ? 'error' : ''}
                    optionalText={t('label.optional')}
                >
                    {() => (
                        <GlowStarRating
                            label={t('label.rating')}
                            id={formInfo.rating.id}
                            name={formInfo.rating.id}
                            onChange={setStarRating}
                            className={'justify-between'}
                        />
                    )}
                </GlowFieldWrapper>

                <GlowDivider />
                <fieldset className={'flex flex-col gap-1.5'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        text={t('label.experience')}
                        color={'primary'}
                        className={'pb-1'}
                    />

                    <div className={'flex flex-col'}>
                        <GlowFieldWrapper
                            label={t('label.ratingTitle')}
                            errorText={error?.title ? 'error' : ''}
                            fieldId={formInfo.title.id}
                            required={formInfo.title.isRequired}
                            optionalText={t('label.optional')}
                            helperText={t('label.titleHelper')}
                        >
                            {() => (
                                <GlowInput
                                    className={clsx(
                                        'flex-1 p-0.75 rounded-0.5 bg-background-form text-text-primary',
                                        'rounded-0.5 focus:outline-bold outline',
                                        error?.title
                                            ? 'outline-border-error outline-bold'
                                            : 'outline-medium outline-border-primary focus:outline-border-selected',
                                    )}
                                    type={'text'}
                                    name={formInfo.title.id}
                                    id={formInfo.title.id}
                                    defaultValue={formState?.data.title?.toString()}
                                    maxLength={REVIEW_TITLE_MAX_LENGTH}
                                />
                            )}
                        </GlowFieldWrapper>
                    </div>

                    <GlowDropDown
                        id={formInfo.usageDuration.id}
                        optionsList={formInfo.usageDuration.options}
                        defaultSelectedValue={t('label.defaultSelectedValueUsageDuration')}
                        title={t('label.productUseDuration')}
                        onValueUpdate={(option) => setUsageDuration(option.value)}
                        ariaLabel={t('label.productUseDuration')}
                        isSelectedFailed={!!error?.usageDuration}
                        required={formInfo.usageDuration.isRequired}
                        optionalText={t('label.optional')}
                    />

                    <GlowTextArea
                        className={'min-w-[18rem]'}
                        errorText={error?.content ? 'error' : ''}
                        label={t('label.content')}
                        id={formInfo.content.id}
                        name={formInfo.content.id}
                        maxLength={REVIEW_DETAILS_MAX_LENGTH}
                        optionalText={t('label.optional')}
                        placeholder={t('label.contentPlaceholder')}
                        defaultValue={formState?.data.content?.toString()}
                        displayMaxLength={false}
                    />
                </fieldset>
                <GlowDivider />
                <fieldset className={'flex flex-col gap-1.5'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        className={'pb-1'}
                        text={t('label.productRecommendation')}
                    />

                    <GlowFieldWrapper fieldId={formInfo.recommended.id} required={formInfo.recommended.isRequired}>
                        {() => (
                            <GlowPills
                                type={'radio'}
                                contents={pills}
                                defaultSelected={defaultPillValue}
                                onChange={setRecommended}
                                className={'flex flex-1 flex-row gap-0.5 w-full'}
                                pillClassName={
                                    'flex flex-1 items-center justify-center before:rounded-0.5 has-[:focus-visible]:rounded-0.5'
                                }
                                pillWrapperClassName={
                                    'w-full gap-0.5 items-center justify-center !rounded-0.5 bg-surface-primary cursor-pointer'
                                }
                            />
                        )}
                    </GlowFieldWrapper>
                </fieldset>
                <GlowDivider />
                <fieldset className={'flex flex-col gap-1.5'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        htmlFor={'rating'}
                        text={t('label.information')}
                        color={'primary'}
                        className={'pb-1'}
                    />

                    <div className={'grid gap-0.5'}>
                        <GlowFieldWrapper
                            label={t('label.nickname')}
                            fieldId={formInfo.nickname.id}
                            required={formInfo.nickname.isRequired}
                            optionalText={t('label.optional')}
                            maxLength={REVIEW_NICKNAME_MAX_LENGTH}
                            displayMaxLength={true}
                            errorText={error?.nickname ? 'error' : ''}
                            helperText={t('label.nicknameHelper')}
                            defaultValue={formState?.data?.nickname?.toString()}
                        >
                            {({ onChange }) => (
                                <GlowInput
                                    className={clsx(
                                        'flex-1 p-0.75 rounded-0.5 bg-background-form text-text-primary',
                                        'rounded-0.5 focus:outline-bold outline',
                                        error?.nickname
                                            ? 'outline-border-error outline-bold'
                                            : 'outline-medium outline-border-primary focus:outline-border-selected',
                                    )}
                                    type={'text'}
                                    name={formInfo.nickname.id}
                                    id={formInfo.nickname.id}
                                    defaultValue={formState?.data?.nickname?.toString()}
                                    maxLength={REVIEW_NICKNAME_MAX_LENGTH}
                                    onChange={(e) => handleChangeNickname(onChange)(e)}
                                />
                            )}
                        </GlowFieldWrapper>
                    </div>

                    <GlowDropDown
                        id={formInfo.age.id}
                        className={clsx(isOpenAgeDropDown ? 'mb-[21rem]' : 'mb-0')}
                        optionsList={formInfo.age.options}
                        defaultSelectedValue={t('label.defaultSelectedValueAge')}
                        title={t('label.age')}
                        onValueUpdate={(option) => setAge(option.value)}
                        ariaLabel={t('label.age')}
                        required={formInfo.age.isRequired}
                        optionalText={t('label.optional')}
                        isSelectedFailed={!!error?.age}
                        isOpenHandler={setIsOpenAgeDropDown}
                    />
                </fieldset>
            </div>

            <div className={'absolute z-10 w-full bg-background-l0 bottom-0 -ml-1'}>
                <GlowDivider />
                <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>
                    <GlowButton
                        className={'flex-1 w-full'}
                        label={t('action.next')}
                        variant={'primary'}
                        formId={RATING_REVIEWS_FORM_ID}
                    />
                </div>
            </div>
        </form>
    );
};
